import { getCJKCharacters, getCJKCharactersLength } from '@/features/bill-payment/utils/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseInputProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value?: string) => void;
  regular?: UseInputRegularVariant | RegExp;
  unicodeCharsLength?: number;
}

interface UseInput {
  value: string | undefined;
  onChange: (value?: string) => void;
  onClear: () => void;
}

export enum UseInputRegularVariant {
  number = 'Number',
  date = 'Date',
  cJKCharacters = 'CJKCharacters'
}

export const useInput = (props: UseInputProps): UseInput => {
  const { onValueChange, regular, unicodeCharsLength } = props;

  const [value, setValue] = useState<string | undefined>(undefined);
  const isMountRef = useRef(true);

  useEffect(() => {
    setValue(props.defaultValue);
    isMountRef.current = false;
  }, []);

  useEffect(() => {
    if (value == undefined) return;
    onValueChange && onValueChange(value);
  }, [value]);

  useEffect(() => {
    if (props.value == undefined) return;
    if (!isMountRef.current) {
      setValue(props.value || '');
      return;
    }
    onChange(props.value);
  }, [props.value]);

  const onChange = useCallback((value?: string) => {
    let input = value || '';
    if (regular) {
      if (typeof regular === 'string') {
        switch (regular) {
          case UseInputRegularVariant.number:
            input = input?.replace(/[^\d]/g, '');
            break;
          case UseInputRegularVariant.date:
            input = input?.replace(/[^\d-]/g, '');
            break;
          case UseInputRegularVariant.cJKCharacters:
            input =
              unicodeCharsLength && getCJKCharactersLength(input || '') > unicodeCharsLength
                ? getCJKCharacters(input || '', unicodeCharsLength)
                : input;
            break;
          default:
            break;
        }
      } else {
        input = input?.replace(regular, '');
      }
    }

    setValue(input);
  }, []);

  function onClear() {
    setValue('');
  }

  return {
    value,
    onChange,
    onClear
  };
};
